import React, { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../../api/http';
import SimplePdfViewer from '../../components/SimplePdfViewer';
import { buildMinioProxyUrl } from '../../utils/authedResource';
import './mobile.css';

const DEFAULT_PREFIX = '培训资料';

const isVideo = (mime, name) => {
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('video/')) return true;
  const n = String(name || '').toLowerCase();
  return n.endsWith('.mp4') || n.endsWith('.webm') || n.endsWith('.mov') || n.endsWith('.m4v') || n.endsWith('.avi');
};
const isPdf = (mime, name) => {
  const m = String(mime || '').toLowerCase();
  if (m === 'application/pdf') return true;
  const n = String(name || '').toLowerCase();
  return n.endsWith('.pdf');
};

const isPpt = (mime, name) => {
  const m = String(mime || '').toLowerCase();
  if (m.includes('powerpoint') || m.includes('presentation')) return true;
  const n = String(name || '').toLowerCase();
  return n.endsWith('.ppt') || n.endsWith('.pptx');
};

const isHttpUrl = (raw) => {
  try {
    const u = new URL(String(raw || '').trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch (e) {
    return false;
  }
};

const normalizeVideoPreviewUrl = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  try {
    const u = new URL(s);
    const host = u.hostname.toLowerCase();
    if (host.includes('youtube.com')) {
      if (u.pathname.startsWith('/embed/')) return u.toString();
      const vid = u.searchParams.get('v');
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    }
    if (host === 'youtu.be') {
      const vid = u.pathname.replace(/^\//, '').trim();
      if (vid) return `https://www.youtube.com/embed/${vid}`;
    }
    return u.toString();
  } catch (e) {
    return '';
  }
};

export default function MobileTrainingPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(null);
  const [planResources, setPlanResources] = useState([]);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await http.get('/files', { params: { prefix: DEFAULT_PREFIX } });
      const list = Array.isArray(res.data?.records) ? res.data.records : [];
      setRecords(list);
    } catch (e) {
      setRecords([]);
      setError(e?.response?.data?.error || e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlanResources = useCallback(async () => {
    try {
      const res = await http.get('/education-training/training-plans');
      const rows = Array.isArray(res.data) ? res.data : [];
      const normalized = rows.flatMap((plan) => {
        const out = [];

        // 新：从文件管理选择的教材（ppt/pdf/视频）
        if (plan?.material_object) {
          const fileName = plan.material_name || `${plan.topic || '培训教材'}`;
          const mimeType = plan.material_mime || '';
          const downloadUrl = String(plan.material_url || '').trim();
          // material_url 为 presigned URL（视频可直接播放）；若缺失则用后端代理兜底（视频可能无法流式播放，但至少可下载/打开）
          const fallbackUrl = buildMinioProxyUrl(plan.material_object, { filename: fileName });
          const finalUrl = downloadUrl || fallbackUrl || '';

          if (finalUrl) {
            out.push({
              kind: 'plan-material',
              source: 'plan',
              id: `plan-material-${plan.id}`,
              title: `${plan.topic || '培训教材'}（教材）`,
              download_url: finalUrl,
              mimeType,
              fileName,
              objectName: plan.material_object
            });
          }
        }

        // 兼容：旧的视频链接
        if (plan?.video_url && isHttpUrl(plan.video_url)) {
          out.push({
            kind: 'plan-video',
            source: 'plan',
            id: `plan-video-${plan.id}`,
            title: `${plan.topic || '培训课程'}（视频）`,
            download_url: String(plan.video_url),
            mimeType: 'text/html',
            fileName: `${plan.topic || '培训课程'} 视频链接`
          });
        }

        // 兼容：旧的上传 PDF
        if (plan?.attachment_url && isHttpUrl(plan.attachment_url)) {
          out.push({
            kind: 'plan-pdf',
            source: 'plan',
            id: `plan-pdf-${plan.id}`,
            title: `${plan.topic || '培训资料'}（PDF）`,
            download_url: String(plan.attachment_url),
            mimeType: 'application/pdf',
            fileName: plan.attachment_name || `${plan.topic || '培训资料'}.pdf`
          });
        }
        return out;
      });
      setPlanResources(normalized);
    } catch (e) {
      setPlanResources([]);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
    fetchPlanResources();
  }, [fetchFiles, fetchPlanResources]);

  const videoList = useMemo(() => {
    const fileVideos = (records || []).filter((r) => isVideo(r?.mimeType, r?.fileName));
    const planVideos = (planResources || []).filter((r) => r.kind === 'plan-video' || (r.kind === 'plan-material' && isVideo(r?.mimeType, r?.fileName)));
    return [...planVideos, ...fileVideos];
  }, [records, planResources]);
  const materialList = useMemo(
    () => {
      const fileMaterials = (records || []).filter((r) => !isVideo(r?.mimeType, r?.fileName));
      const planMaterials = (planResources || []).filter((r) => {
        if (r.kind === 'plan-pdf') return true;
        if (r.kind === 'plan-material') return !isVideo(r?.mimeType, r?.fileName);
        return false;
      });
      return [...planMaterials, ...fileMaterials];
    },
    [records, planResources]
  );

  const openItem = (r) => {
    if (!r?.download_url) return;
    setSelected(r);
  };

  return (
    <div className="m-section">
      <div className="m-card m-cardPad">
        <div className="m-h2">教育培训</div>
        <div className="m-muted">从“{DEFAULT_PREFIX}”目录加载视频与资料（若为空请联系管理员上传）。</div>
        <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={fetchFiles} disabled={loading}>
            {loading ? '刷新中...' : '刷新'}
          </button>
          <a className="btn btn-outline" href="/m/me/training-records">查看我的培训记录</a>
        </div>
        {error ? <div className="m-alert m-alertErr" style={{ marginTop: 10 }}>{error}</div> : null}
      </div>

      <div className="m-card m-cardPad">
        <div className="m-h2">视频/课程</div>
        {videoList.length === 0 ? (
          <div className="m-alert">暂无视频课程</div>
        ) : (
          <div className="m-list">
            {videoList.map((r) => (
              <button
                key={r.id || r.objectName || r.fileName}
                className="btn btn-outline"
                style={{ justifyContent: 'space-between' }}
                onClick={() => openItem(r)}
              >
                <span style={{ textAlign: 'left' }}>{r.title || r.fileName || r.objectName}</span>
                <span className="badge">播放</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="m-card m-cardPad">
        <div className="m-h2">培训资料</div>
        {materialList.length === 0 ? (
          <div className="m-alert">暂无资料</div>
        ) : (
          <div className="m-list">
            {materialList.map((r) => (
              <button
                key={r.id || r.objectName || r.fileName}
                className="btn btn-outline"
                style={{ justifyContent: 'space-between' }}
                onClick={() => openItem(r)}
              >
                <span style={{ textAlign: 'left' }}>{r.title || r.fileName || r.objectName}</span>
                <span className="badge">查看</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected?.download_url ? (
        <div className="m-card m-cardPad">
          <div className="m-h2">预览</div>
          <div className="m-muted" style={{ marginBottom: 10 }}>{selected.title || selected.fileName || selected.objectName}</div>

          {isVideo(selected.mimeType, selected.fileName) ? (
            <video
              controls
              playsInline
              style={{ width: '100%', borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}
              src={selected.download_url}
            />
          ) : selected.kind === 'plan-video' ? (
            <div style={{ width: '100%', height: 280, borderRadius: 12, border: '1px solid var(--border-color)', overflow: 'hidden', background: '#000' }}>
              <iframe
                title="mobile-plan-video"
                src={normalizeVideoPreviewUrl(selected.download_url)}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allowFullScreen
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                referrerPolicy="no-referrer"
              />
            </div>
          ) : isPdf(selected.mimeType, selected.fileName) ? (
            <SimplePdfViewer url={selected.download_url} fileName={selected.fileName || ''} />
          ) : isPpt(selected.mimeType, selected.fileName) ? (
            <div className="m-alert">
              PPT 暂不支持内嵌预览，
              <a href={selected.download_url} target="_blank" rel="noreferrer">点此打开/下载</a>
            </div>
          ) : (
            <div className="m-alert">
              当前类型不支持内嵌预览，
              <a href={selected.download_url} target="_blank" rel="noreferrer">点此打开/下载</a>
            </div>
          )}

        </div>
      ) : null}
    </div>
  );
}
